/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  faChevronDoubleRight,
  faSearchPlus,
  faSlidersH,
  faTimes
} from '@fortawesome/pro-regular-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { EmployeeModel } from '../../../models/employee.model';
import { WorkflowModel } from '../../../models/workflow.model';
import { CustomDateFormatService } from '../../../services/custom-date-format/custom-date-format.service';
import { EmployeeService } from '../../../services/employee.service';
import { WorkflowService } from '../../../services/workflow.service';
import { GalleryFilter } from '../../shared/oneapp-task-image-grid/oneapp-task-image-grid.component';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  standalone: false
})
export class GalleryComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();

  readonly faSlidersH = faSlidersH;
  readonly faTimes = faTimes;
  readonly faChevronDoubleRight = faChevronDoubleRight;
  readonly faSearchPlus = faSearchPlus;

  filterOpen = true;

  workflows: WorkflowModel[] = [];
  employees: EmployeeModel[] = [];

  filterOptions = new FormGroup({
    fromDate: new FormControl(new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)), {
      nonNullable: true
    }),
    fromTime: new FormControl('00:00', { nonNullable: true }),
    toDate: new FormControl(new Date(), { nonNullable: true }),
    toTime: new FormControl('23:59', { nonNullable: true }),
    workflowId: new FormControl('', { nonNullable: true }),
    employeeId: new FormControl<string[]>([], { nonNullable: true })
  });

  filter: GalleryFilter = {
    from: new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)),
    to: new Date(),
    workflowId: '',
    employeeId: []
  };

  constructor(
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    public customDateFormatService: CustomDateFormatService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit(): Promise<void> {
    this.workflows = await this.workflowService.searchWorkflow({
      search_criteria: {},
      limit: 0,
      skip: 0,
      sort: 'name',
      select: '_id name version'
    });

    this.employeeService.employeesChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((newEmployees) => {
        this.employees = newEmployees;
      });
    await this.employeeService.getEmployees();
  }

  onFilterClicked(): void {
    this.filter = {
      from: new Date(
        this.getIsoStringFromDateAndTimeString(
          this.filterOptions.controls.fromDate.value,
          this.filterOptions.controls.fromTime.value
        )
      ),
      to: new Date(
        this.getIsoStringFromDateAndTimeString(
          this.filterOptions.controls.toDate.value,
          this.filterOptions.controls.toTime.value
        )
      ),
      workflowId: this.filterOptions.controls.workflowId.value,
      employeeId: this.filterOptions.controls.employeeId.value
    };
  }

  // Updates a date object with a string representation of time
  getIsoStringFromDateAndTimeString(date: Date, time: string): string {
    const [hoursString, minutesString] = time.split(':');
    const hours = parseInt(hoursString, 10);
    const minutes = parseInt(minutesString, 10);

    const dateWithTime = new Date(date.getTime());
    dateWithTime.setHours(hours);
    dateWithTime.setMinutes(minutes);
    dateWithTime.setSeconds(0);
    dateWithTime.setMilliseconds(0);

    return dateWithTime.toISOString();
  }
}
